/**
 * Sammelt Messreihen und gibt sie als CSV aus (W12).
 *
 * Begrenzter Speicher
 * -------------------
 * Ein Roboter, der eine Stunde laeuft, sendet mehr Messpunkte, als sich sinnvoll
 * im Browser halten lassen. Der Speicher ist deshalb begrenzt und verwirft die
 * aeltesten Punkte. Was verworfen wurde, wird gezaehlt: ein Diagramm, das
 * stillschweigend nur die letzten Minuten zeigt, waere irrefuehrend.
 *
 * Luecken
 * -------
 * Ein Messwert kann fehlen, ohne dass die Messung fehlt - etwa wenn der
 * Ultraschall kein Echo bekommt. Solche Punkte werden als `null` gefuehrt und
 * im Diagramm als Luecke gezeichnet, nicht als Null (G08).
 */

import type { OrientationShow, TelemetrySample } from "./parse";

/** Einheiten der Standardkanaele, fuer Beschriftung und CSV (W12). */
export const UNITS: Record<string, string> = {
  battery_v: "V",
  tilt_rad: "rad",
  distance_m: "m",
  speed_left_rad_s: "rad/s",
  speed_right_rad_s: "rad/s",
  x_m: "m",
  y_m: "m",
  heading_rad: "rad",
};

/** Hoechste Anzahl behaltener Messpunkte. */
export const DEFAULT_CAPACITY = 3000;

export interface Channel {
  name: string;
  unit: string;
  /** Werte in derselben Reihenfolge wie die Zeiten; `null` ist eine Luecke. */
  values: (number | null)[];
}

export class TelemetrySeries {
  private times: number[] = [];
  private channels = new Map<string, (number | null)[]>();
  private droppedOld = 0;
  private lastSequence: number | undefined;
  private missed = 0;
  private orientationShow: OrientationShow = "both";

  constructor(private readonly capacity: number = DEFAULT_CAPACITY) {}

  add(sample: TelemetrySample): void {
    if (this.lastSequence !== undefined) {
      const gap = sample.sequence - this.lastSequence - 1;
      if (gap > 0) {
        this.missed += gap;
      }
    }
    this.lastSequence = sample.sequence;
    // Nur Zeilen mit Lage bestimmen, wie sie gezeigt wird. Eine Zeile ohne
    // Lage - etwa nur der Standardsatz - sagt darueber nichts, und die Anzeige
    // soll nicht bei jeder solchen Zeile auf "beides" zurueckspringen.
    if ("q_w" in sample.values) {
      this.orientationShow = sample.show ?? "both";
    }

    this.times.push(sample.time);
    // Neue Kanaele koennen jederzeit dazukommen, etwa durch telemetry.send().
    // Sie beginnen mit Luecken, damit alle Reihen gleich lang bleiben.
    for (const name of Object.keys(sample.values)) {
      if (!this.channels.has(name)) {
        this.channels.set(
          name,
          new Array(this.times.length - 1).fill(null) as (number | null)[]
        );
      }
    }
    for (const [name, values] of this.channels) {
      const value = sample.values[name];
      values.push(value === undefined ? null : value);
    }

    if (this.times.length > this.capacity) {
      const excess = this.times.length - this.capacity;
      this.times.splice(0, excess);
      for (const values of this.channels.values()) {
        values.splice(0, excess);
      }
      this.droppedOld += excess;
    }
  }

  /** Zeiten der behaltenen Messpunkte, in Sekunden. */
  timestamps(): number[] {
    return this.times;
  }

  /** Alle Kanaele mit Einheit. */
  list(): Channel[] {
    const result: Channel[] = [];
    for (const [name, values] of this.channels) {
      result.push({ name, unit: UNITS[name] ?? "", values });
    }
    return result;
  }

  channel(name: string): Channel | undefined {
    const values = this.channels.get(name);
    return values ? { name, unit: UNITS[name] ?? "", values } : undefined;
  }

  count(): number {
    return this.times.length;
  }

  /** Wie viele Messpunkte die Firmware verworfen hat (Luecken der Folge). */
  missedCount(): number {
    return this.missed;
  }

  /** Wie das Programm die Lage gezeigt haben will (send_orientation). */
  orientationDisplay(): OrientationShow {
    return this.orientationShow;
  }

  /** Wie viele alte Messpunkte der Speicher verworfen hat. */
  droppedCount(): number {
    return this.droppedOld;
  }

  clear(): void {
    this.times = [];
    this.channels.clear();
    this.droppedOld = 0;
    this.missed = 0;
    this.lastSequence = undefined;
    this.orientationShow = "both";
  }
}

/**
 * Schreibt die Reihen als CSV.
 *
 * Die Einheit steht in der Kopfzeile beim Namen, damit die Datei ohne diese
 * Oberflaeche verstaendlich bleibt. Fehlende Messwerte bleiben leer statt zu
 * einer Null zu werden: eine Tabellenkalkulation soll sie nicht mitrechnen.
 */
export function toCsv(series: TelemetrySeries): string {
  const channels = series.list();
  const times = series.timestamps();
  const header = ["t (s)", ...channels.map((c) => (c.unit ? `${c.name} (${c.unit})` : c.name))];
  const lines = [header.join(",")];
  for (let row = 0; row < times.length; row += 1) {
    const cells = [String(times[row])];
    for (const channel of channels) {
      const value = channel.values[row];
      cells.push(value === null || value === undefined ? "" : String(value));
    }
    lines.push(cells.join(","));
  }
  return lines.join("\n") + "\n";
}
